import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {Component, Inject} from '@angular/core';
import {AbstractControl, FormControl, ValidationErrors, ValidatorFn} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {Team} from "../../interface/team";
import {InvitationService} from "../../service/invitation.service";
import {MultipleInvitationRequest} from "../../interface/multipleInvitationRequest";
import {HttpErrorResponse} from "@angular/common/http";
import {MESSAGE_TIMEOUT_3S} from "../../constants/global.constants";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-add-new-member-in-team',
  templateUrl: './add-new-member-in-team.component.html',
  styleUrl: './add-new-member-in-team.component.scss'
})
export class AddNewMemberInTeamComponent {

  public readonly VALIDATION_ERROR_TEXT = 'my_team.validation_error_text';
  public readonly VALIDATION_EMPTY_TEXT = 'my_team.validation_empty_text';
  public readonly VALIDATION_ERROR_ALREADY_SEND_TO_EMAIL_TEXT = 'my_team.validation_error_already_sent_text';
  public readonly VALIDATION_ERROR_ALREADY_MEMBER_TEXT = 'my_team.validation_error_already_member_text';
  public readonly VALIDATION_ERROR_USERNAME_NOT_EXIST = 'my_team.validation_error_username_not_exist';
  public readonly SUCCESS_MESSAGE = 'my_team.success_message';
  public readonly EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  public readonly USERNAME_PATTERN = /^@[a-zA-Z]{3}\d{3}$/i;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  form = new FormControl();
  members: string[] = [];

  hasMemberValidationError: boolean = false;
  isMembersEmpty: boolean = false;
  hasInvitationAlreadySentError: boolean = false;
  hasAlreadyMemberError: boolean = false;
  hasUsernameNotExistError: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { userCreatedTeam: Team },
    protected dialogRef: MatDialogRef<AddNewMemberInTeamComponent>,
    private invitationService: InvitationService,
    private snackBarUtil: SnackBarUtil,
    private translateService: TranslateService
  ) {
    this.form = new FormControl('', [this.emailOrIdValidator()]);
    this.form.valueChanges.subscribe(() => {
      this.hasMemberValidationError = false;
      this.hasInvitationAlreadySentError = false;
      this.hasAlreadyMemberError = false;
      this.hasUsernameNotExistError = false;
    });
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  add(event: any): void {
    const input = event.input;
    const value = (event.value || '').trim();

    if (this.form.invalid || !value) {
      return;
    }

    if (this.USERNAME_PATTERN.test(value)) {
      this.invitationService.checkUsernameExists(value).subscribe({
        next: (exists: boolean) => {
          if (exists) {
            this.hasUsernameNotExistError = false;
            this.members.push(value);
            if (input) input.value = '';
            this.form.setValue(null);
          } else {
            this.hasUsernameNotExistError = true;
          }
        },
        error: () => {
          this.hasUsernameNotExistError = true;
        }
      });
    } else {
      this.members.push(value);
      if (input) input.value = '';
      this.form.setValue(null);
    }
  }

  remove(member: string): void {
    const index = this.members.indexOf(member);

    if (index >= 0) {
      this.members.splice(index, 1);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.members.length === 0) {
      this.isMembersEmpty = true;
      return;
    }
    if (this.validateMembers()) {
      const request: MultipleInvitationRequest = {
        teamId: this.data.userCreatedTeam.id,
        toUsernames: this.members
      };
      this.invitationService.sendMultipleInvitations(request).subscribe({
        next: () => {
          this.showSnackbar(
            MockupDataModel.success(this.translateService.instant(this.SUCCESS_MESSAGE)),
            SnackBarUtil.CLASSES.success
          )
          setTimeout(() => {
            this.dialogRef.close();
          }, MESSAGE_TIMEOUT_3S);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 422) {
            this.hasAlreadyMemberError = true;
          } else {
            this.hasInvitationAlreadySentError = true;
          }
        }
      });
    }
  }

  private validateMembers(): boolean {
    this.hasMemberValidationError = this.members.some(member => {
      return !this.isValidEmailOrId(member);
    });
    return !this.hasMemberValidationError;
  }

  private isValidEmailOrId(value: string): boolean {
    return this.EMAIL_PATTERN.test(value) || this.USERNAME_PATTERN.test(value);
  }

  private emailOrIdValidator(): ValidatorFn {
    this.hasMemberValidationError = false;
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const isValid = this.EMAIL_PATTERN.test(value) || this.USERNAME_PATTERN.test(value);
      return isValid ? null : {emailOrId: true};
    };
  }
}
